import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { photoUrl } from "@/lib/apiClient";
import { mapsUrl } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, MapPin, Clock, CalendarDays, UserPlus, X, Camera, Tags, Check, Power, ExternalLink, Network, GitBranch } from "lucide-react";
import { toast } from "sonner";

function PhotoUpload({ group, onDone, overlay }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/groups/${group.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto atualizada!");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao enviar foto");
    }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <label
      className={overlay
        ? "absolute bottom-2 right-2 h-9 w-9 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center cursor-pointer hover:bg-black/80 transition-colors"
        : "flex items-center gap-2 text-sm text-primary cursor-pointer hover:underline"}
      data-testid={`group-photo-upload-${group.id}`}
    >
      <Camera className="h-4 w-4" />
      {!overlay && <span>{busy ? "Enviando…" : "Adicionar foto"}</span>}
      <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
    </label>
  );
}

const DAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
const CATS = ["Misto", "Casais", "Jovens", "Homens", "Mulheres", "Adolescentes"];

const emptyGroup = {
  name: "", leader_id: "", coleader_id: "", secretario_id: "", anfitriao_id: "", supervisor_id: "", parent_group_id: "",
  day_of_week: "Terça", time: "19:30", start_date: "",
  neighborhood: "", address: "", category: "Misto", capacity: 12, description: "", active: true,
};

function GroupForm({ leaders, people, categories, groups = [], initial, onSaved, canAssignPeople = true }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || emptyGroup);
  const isEdit = !!initial;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      capacity: Number(form.capacity) || 12,
      leader_id: form.leader_id || null,
      coleader_id: form.coleader_id || null,
      secretario_id: form.secretario_id || null,
      anfitriao_id: form.anfitriao_id || null,
      supervisor_id: form.supervisor_id || null,
      parent_group_id: form.parent_group_id || null,
      start_date: form.start_date || null,
    };
    try {
      if (isEdit) await api.put(`/groups/${initial.id}`, payload);
      else await api.post("/groups", payload);
      toast.success(isEdit ? "Grupo atualizado" : "Grupo criado");
      setOpen(false);
      if (!isEdit) setForm(emptyGroup);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon" data-testid={`edit-group-${initial.id}`}><Pencil className="h-4 w-4" /></Button>
        ) : (
          <Button className="gap-2" data-testid="add-group-button"><Plus className="h-4 w-4" /> Novo Grupo</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? "Editar Grupo" : "Novo Pequeno Grupo"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nome do grupo</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 col-span-2" data-testid="group-name-input" />
            </div>
          </div>
          {canAssignPeople && (
          <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Líder</Label>
              <Select value={form.leader_id || ""} onValueChange={(v) => set("leader_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="group-leader-assign-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {leaders.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Co-líder</Label>
              <Select value={form.coleader_id || ""} onValueChange={(v) => set("coleader_id", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Opcional" /></SelectTrigger>
                <SelectContent>
                  {leaders.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Secretário(a)</Label>
              <Select value={form.secretario_id || ""} onValueChange={(v) => set("secretario_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="group-secretario-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {people.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Anfitrião(ã)</Label>
              <Select value={form.anfitriao_id || ""} onValueChange={(v) => set("anfitriao_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="group-anfitriao-select"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {people.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Supervisor</Label>
            <Select value={form.supervisor_id || ""} onValueChange={(v) => set("supervisor_id", v)}>
              <SelectTrigger className="mt-1.5" data-testid="group-supervisor-select"><SelectValue placeholder="Selecionar (responsável pela supervisão)" /></SelectTrigger>
              <SelectContent>
                {people.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Grupo de origem (multiplicação)</Label>
            <Select value={form.parent_group_id || ""} onValueChange={(v) => set("parent_group_id", v)}>
              <SelectTrigger className="mt-1.5" data-testid="group-parent-select"><SelectValue placeholder="De qual PGD este grupo nasceu? (opcional)" /></SelectTrigger>
              <SelectContent>
                {groups.filter((g) => !initial || g.id !== initial.id).map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          </>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Dia</Label>
              <Select value={form.day_of_week} onValueChange={(v) => set("day_of_week", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Horário</Label>
              <Input value={form.time} onChange={(e) => set("time", e.target.value)} className="mt-1.5" placeholder="19:30" />
            </div>
          </div>
          <div>
            <Label>Data de início (opcional)</Label>
            <Input type="date" value={form.start_date || ""} onChange={(e) => set("start_date", e.target.value)} className="mt-1.5" data-testid="group-start-date-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{(categories && categories.length ? categories.map((c) => c.name) : CATS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Capacidade</Label>
              <Input type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} className="mt-1.5" min={1} max={30} />
            </div>
          </div>
          <div>
            <Label>Bairro</Label>
            <Input value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Endereço</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="save-group-button">{isEdit ? "Salvar" : "Criar grupo"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({ group, onChanged, canManage }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [pickedUser, setPickedUser] = useState("");

  const load = useCallback(() => {
    api.get("/members", { params: { group_id: group.id } }).then((r) => setMembers(r.data));
  }, [group.id]);

  useEffect(() => {
    if (open) {
      load();
      if (canManage) api.get("/users/directory").then((r) => setDirectory(r.data)).catch(() => setDirectory([]));
    }
  }, [open, load, canManage]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await api.post("/members", { name, phone, group_id: group.id, is_visitor: false });
    setName(""); setPhone("");
    load(); onChanged();
  };

  const addFromDirectory = async () => {
    const u = directory.find((d) => d.id === pickedUser);
    if (!u) return toast.error("Selecione uma pessoa do cadastro");
    if (members.some((m) => m.user_id === u.id)) return toast.error("Essa pessoa já está no grupo");
    await api.post("/members", { name: u.name, phone: u.phone || "", group_id: group.id, user_id: u.id, is_visitor: false });
    setPickedUser("");
    toast.success(`${u.name} adicionado(a)`);
    load(); onChanged();
  };

  const remove = async (id) => {
    await api.delete(`/members/${id}`);
    load(); onChanged();
  };

  const available = directory.filter((d) => !members.some((m) => m.user_id === d.id));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid={`manage-members-${group.id}`}>
          <UserPlus className="h-4 w-4" /> Participantes
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Participantes · {group.name}</DialogTitle>
          <DialogDescription>Inclua pessoas do cadastro ou cadastre novos participantes.</DialogDescription>
        </DialogHeader>
        {canManage && (
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Incluir do cadastro</div>
              <div className="flex gap-2">
                <Select value={pickedUser} onValueChange={setPickedUser}>
                  <SelectTrigger data-testid="member-directory-select"><SelectValue placeholder="Escolher pessoa cadastrada" /></SelectTrigger>
                  <SelectContent>
                    {available.length === 0
                      ? <div className="px-2 py-3 text-sm text-muted-foreground text-center">Ninguém disponível no cadastro</div>
                      : available.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.role ? ` · ${u.role}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" onClick={addFromDirectory} className="gap-1.5" data-testid="add-member-from-directory"><Plus className="h-4 w-4" /> Incluir</Button>
              </div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Novo participante</div>
              <form onSubmit={add} className="flex gap-2">
                <Input placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} data-testid="member-name-input" />
                <Input placeholder="Telefone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-40" />
                <Button type="submit" size="icon" data-testid="add-member-button"><Plus className="h-4 w-4" /></Button>
              </form>
            </div>
          </div>
        )}
        <div className="space-y-2 mt-2">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div>
                <span className="font-medium text-sm">{m.name}</span>
                {m.user_id && <Badge variant="secondary" className="ml-2 text-[10px]">do cadastro</Badge>}
                {m.phone && <span className="text-xs text-muted-foreground ml-2">{m.phone}</span>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => remove(m.id)} disabled={!canManage} data-testid={`remove-member-${m.id}`}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          {members.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nenhum participante ainda.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoriesDialog({ categories, onChanged }) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({});

  const add = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await api.post("/categories", { name: newName.trim() });
      setNewName("");
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar");
    }
  };
  const rename = async (c) => {
    const name = (editing[c.id] ?? c.name).trim();
    if (!name || name === c.name) { setEditing((e) => ({ ...e, [c.id]: undefined })); return; }
    await api.put(`/categories/${c.id}`, { name });
    setEditing((e) => ({ ...e, [c.id]: undefined }));
    onChanged();
    toast.success("Categoria atualizada");
  };
  const remove = async (c) => {
    await api.delete(`/categories/${c.id}`);
    onChanged();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="manage-categories-button"><Tags className="h-4 w-4" /> Categorias</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Gerenciar categorias</DialogTitle></DialogHeader>
        <form onSubmit={add} className="flex gap-2">
          <Input placeholder="Nova categoria" value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-category-input" />
          <Button type="submit" size="icon" data-testid="add-category-button"><Plus className="h-4 w-4" /></Button>
        </form>
        <div className="space-y-2 mt-2">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2" data-testid="category-row">
              <Input
                value={editing[c.id] ?? c.name}
                onChange={(e) => setEditing((s) => ({ ...s, [c.id]: e.target.value }))}
                className="h-8 flex-1"
                data-testid={`category-name-${c.id}`}
              />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => rename(c)} data-testid={`save-category-${c.id}`}><Check className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(c)} data-testid={`delete-category-${c.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
          {categories.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma categoria ainda.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GroupCard({ g, canEdit, isAdmin, leaders, people, categories, groups, onReload, onToggle, onDelete }) {
  const url = mapsUrl(g.address, g.neighborhood);
  const label = [g.address, g.neighborhood].filter(Boolean).join(" · ");
  return (
    <Card className="overflow-hidden flex flex-col" data-testid="group-card-item">
      {g.photo_path ? (
        <div className="relative h-36 w-full">
          <img src={photoUrl(g.photo_path)} alt={g.name} className="h-36 w-full object-cover" />
          {canEdit && <PhotoUpload group={g} onDone={onReload} overlay />}
        </div>
      ) : (
        <div className="h-24 w-full bg-gradient-to-br from-primary/15 to-accent/10 flex items-center justify-center">
          {canEdit ? <PhotoUpload group={g} onDone={onReload} /> : <Users className="h-8 w-8 text-primary/40" />}
        </div>
      )}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-semibold">{g.name}</h3>
          <Badge variant={g.active ? "default" : "secondary"}>{g.active ? "Ativo" : "Inativo"}</Badge>
        </div>
        <Badge variant="secondary" className="w-fit mt-2">{g.category}</Badge>
        <div className="mt-4 space-y-2 text-sm text-muted-foreground flex-1">
          <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> {g.leader_name || "Sem líder"}</div>
          {g.coleader_name && <div className="flex items-center gap-2 pl-6 text-xs">Co-líder: {g.coleader_name}</div>}
          {g.secretario_name && <div className="flex items-center gap-2 pl-6 text-xs">Secretário(a): {g.secretario_name}</div>}
          {g.anfitriao_name && <div className="flex items-center gap-2 pl-6 text-xs">Anfitrião(ã): {g.anfitriao_name}</div>}
          {(g.address || g.neighborhood) ? (
            url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 text-primary hover:underline" data-testid={`group-map-link-${g.id}`}>
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{label} <ExternalLink className="inline h-3 w-3 mb-0.5" /></span>
              </a>
            ) : (
              <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" /> {label}</div>
            )
          ) : (
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> —</div>
          )}
          <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> {g.day_of_week}{g.start_date ? ` · início em ${g.start_date.slice(8, 10)}/${g.start_date.slice(5, 7)}/${g.start_date.slice(0, 4)}` : ""}</div>
          <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> {g.time}</div>
          {g.parent_group_name && <div className="flex items-center gap-2 text-xs"><GitBranch className="h-3.5 w-3.5 text-primary" /> Multiplicado de: {g.parent_group_name}</div>}
          <div className="flex items-center gap-2"><span className="font-medium text-foreground">{g.member_count}</span>/{g.capacity} membros</div>
        </div>
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <MembersDialog group={g} onChanged={onReload} canManage={canEdit} />
          {canEdit && (
            <>
              <GroupForm leaders={leaders} people={people} categories={categories} groups={groups} initial={g} onSaved={onReload} canAssignPeople={isAdmin} />
              <Button variant="ghost" size="sm" className={`gap-1.5 ${g.active ? "text-muted-foreground" : "text-[hsl(var(--emerald))]"}`} onClick={() => onToggle(g)} data-testid={`toggle-active-group-${g.id}`}>
                <Power className="h-4 w-4" /> {g.active ? "Desativar" : "Ativar"}
              </Button>
            </>
          )}
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" data-testid={`delete-group-${g.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remover {g.name}?</AlertDialogTitle>
                  <AlertDialogDescription>Isso removerá o grupo, seus membros e registros de frequência.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(g.id)} data-testid="confirm-delete-group">Remover</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>
    </Card>
  );
}


function MultiplicationTree({ groups }) {
  const [open, setOpen] = useState(false);
  const ids = new Set(groups.map((g) => g.id));
  const roots = groups.filter((g) => !g.parent_group_id || !ids.has(g.parent_group_id));
  const anyChild = groups.some((g) => g.parent_group_id && ids.has(g.parent_group_id));

  const flat = [];
  const walk = (node, depth) => {
    flat.push({ node, depth });
    groups.filter((g) => g.parent_group_id === node.id).forEach((c) => walk(c, depth + 1));
  };
  roots.forEach((r) => walk(r, 0));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="mult-tree-button"><Network className="h-4 w-4" /> Árvore de Multiplicação</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Árvore de Multiplicação dos PGDs</DialogTitle></DialogHeader>
        {!anyChild && <p className="text-sm text-muted-foreground">Nenhuma multiplicação registrada ainda. Ao criar/editar um grupo, informe o "Grupo de origem".</p>}
        <div className="space-y-1">
          {flat.map(({ node, depth }) => (
            <div key={node.id} className="flex items-center gap-2 py-1.5" style={{ paddingLeft: `${depth * 20}px` }} data-testid="mult-tree-node">
              <GitBranch className={`h-4 w-4 shrink-0 ${depth === 0 ? "text-primary" : "text-muted-foreground"}`} />
              <span className="font-medium text-sm">{node.name}</span>
              {!node.active && <Badge variant="secondary" className="text-[10px]">Inativo</Badge>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [people, setPeople] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("ativos");
  const isAdmin = !!user?.is_admin || user?.role === "coordenador";

  const canEditGroup = useCallback((g) => {
    if (isAdmin) return true;
    const uid = String(user?.id);
    return [g.leader_id, g.coleader_id, g.secretario_id, g.anfitriao_id, g.supervisor_id]
      .filter(Boolean).map(String).includes(uid);
  }, [isAdmin, user]);

  const load = useCallback(() => {
    const params = isAdmin ? {} : { mine: true };
    api.get("/groups", { params }).then((r) => setGroups(r.data));
  }, [isAdmin]);
  const loadCats = useCallback(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    loadCats();
    if (isAdmin) {
      api.get("/users", { params: { role: "lider" } }).then((r) => setLeaders(r.data)).catch(() => {});
      api.get("/users").then((r) => setPeople(r.data)).catch(() => {});
    }
  }, [load, loadCats, isAdmin]);

  const del = async (id) => {
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Grupo removido");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao remover"); }
  };

  const toggleActive = async (g) => {
    try {
      await api.patch(`/groups/${g.id}/active`, { active: !g.active });
      toast.success(g.active ? "Grupo desativado" : "Grupo ativado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao alterar status"); }
  };

  const visibleGroups = groups.filter((g) =>
    filter === "ativos" ? g.active : filter === "inativos" ? !g.active : true
  );

  return (
    <div className="space-y-8 animate-fade-up" data-testid="groups-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Grupos & Líderes</h1>
          <p className="text-muted-foreground mt-1">Gestão dos Pequenos Grupos de Discipulado.</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <MultiplicationTree groups={groups} />
            <CategoriesDialog categories={categories} onChanged={() => { loadCats(); load(); }} />
            <GroupForm leaders={leaders} people={people} categories={categories} groups={groups} onSaved={load} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 rounded-lg bg-muted p-1 w-fit" data-testid="group-status-filter">
        {[["ativos", "Ativos"], ["inativos", "Inativos"], ["todos", "Todos"]].map(([key, label]) => {
          const count = key === "ativos" ? groups.filter((g) => g.active).length
            : key === "inativos" ? groups.filter((g) => !g.active).length : groups.length;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors ${filter === key ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`filter-${key}`}
            >
              {label} <span className="text-xs opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="groups-list-table">
        {visibleGroups.map((g) => (
          <GroupCard
            key={g.id}
            g={g}
            canEdit={canEditGroup(g)}
            isAdmin={isAdmin}
            leaders={leaders}
            people={people}
            categories={categories}
            groups={groups}
            onReload={load}
            onToggle={toggleActive}
            onDelete={del}
          />
        ))}
      </div>
      {visibleGroups.length === 0 && (
        <p className="text-muted-foreground" data-testid="groups-empty">
          {groups.length === 0 ? "Nenhum grupo cadastrado." : `Nenhum grupo ${filter === "inativos" ? "inativo" : filter === "ativos" ? "ativo" : ""} no momento.`}
        </p>
      )}
    </div>
  );
}
